import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import {
  ArrowLeft, MapPin, Calendar, Phone, Mail, IndianRupee,
  FileText, Clock, CreditCard, Plus, CheckCircle, XCircle,
  Sparkles, Loader2, Trash2, Download, FileDown, Copy,
  Edit3, Eye, Save, PlaneTakeoff, PlaneLanding, Hotel, RefreshCw,
  Users, Briefcase, Tag, CalendarDays, Palette,
  Upload, Heart, Building2, Mountain, LayoutTemplate, Globe, DollarSign, Check, X
} from 'lucide-react';

const STAGES = ['new', 'quotation_sent', 'followup', 'negotiation', 'confirmed', 'lost'];
const STAGE_CONFIG = {
  new: { label: 'New', bg: 'bg-blue-500', text: 'text-blue-700', lightBg: 'bg-blue-50 dark:bg-blue-950/40' },
  quotation_sent: { label: 'Quotation Sent', bg: 'bg-violet-500', text: 'text-violet-700', lightBg: 'bg-violet-50 dark:bg-violet-950/40' },
  followup: { label: 'Follow-up', bg: 'bg-amber-500', text: 'text-amber-700', lightBg: 'bg-amber-50 dark:bg-amber-950/40' },
  negotiation: { label: 'Negotiation', bg: 'bg-orange-500', text: 'text-orange-700', lightBg: 'bg-orange-50 dark:bg-orange-950/40' },
  confirmed: { label: 'Confirmed', bg: 'bg-emerald-500', text: 'text-emerald-700', lightBg: 'bg-emerald-50 dark:bg-emerald-950/40' },
  lost: { label: 'Lost', bg: 'bg-red-500', text: 'text-red-700', lightBg: 'bg-red-50 dark:bg-red-950/40' }
};

export default function LeadDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [quotations, setQuotations] = useState([]);
  const [followups, setFollowups] = useState([]);
  const [payments, setPayments] = useState([]);
  const [itineraries, setItineraries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showQuotation, setShowQuotation] = useState(false);
  const [showFollowup, setShowFollowup] = useState(false);
  const [showPayment, setShowPayment] = useState(false);

  const [showClosureDialog, setShowClosureDialog] = useState(false);
  const [closureStage, setClosureStage] = useState('');
  const [closureReason, setClosureReason] = useState('');

  const [rawText, setRawText] = useState('');
  const [specialInstructions, setSpecialInstructions] = useState('');
  const [itinType, setItinType] = useState('detailed');
  const [itinTone, setItinTone] = useState('professional');
  const [arrivalDate, setArrivalDate] = useState('');
  const [departureDate, setDepartureDate] = useState('');
  const [destinationInput, setDestinationInput] = useState('');
  const [generating, setGenerating] = useState(false);
  const [showHotelConfirm, setShowHotelConfirm] = useState(false);

  const [formattedItinerary, setFormattedItinerary] = useState(null);
  const [showItineraryEditor, setShowItineraryEditor] = useState(false);
  const [manualPricing, setManualPricing] = useState([{ description: '', amount: '' }]);
  const [itineraryTotal, setItineraryTotal] = useState('');
  const [downloadingId, setDownloadingId] = useState(null);
  const [editingItineraryId, setEditingItineraryId] = useState(null);
  const [showHotelPopup, setShowHotelPopup] = useState(false);
  const [pendingSaveAsDraft, setPendingSaveAsDraft] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [currency, setCurrency] = useState('INR');

  const [hotelDetails, setHotelDetails] = useState([]);
  const [termsConditions, setTermsConditions] = useState([]);

  const [newQuotation, setNewQuotation] = useState({ items: [{ description: '', type: 'hotel', amount: 0 }], total_amount: 0, currency: 'INR', notes: '' });
  const [newFollowup, setNewFollowup] = useState({ type: 'call', scheduled_at: '', notes: '' });
  const [newPayment, setNewPayment] = useState({ amount: 0, payment_method: 'bank_transfer', reference: '', notes: '' });

  const fetchAll = useCallback(async () => {
    try {
      const [leadRes, quotRes, followRes, payRes, itinRes] = await Promise.all([
        api.get(`/leads/${id}`),
        api.get('/quotations', { params: { lead_id: id } }),
        api.get('/followups', { params: { lead_id: id } }),
        api.get('/payments', { params: { lead_id: id } }),
        api.get('/itineraries', { params: { lead_id: id } })
      ]);
      setLead(leadRes.data);
      setQuotations(quotRes.data);
      setFollowups(followRes.data);
      setPayments(payRes.data);
      setItineraries(itinRes.data);
      if (leadRes.data?.destination) setDestinationInput(leadRes.data.destination);
      // Auto-fill travel dates from lead
      if (leadRes.data?.travel_date) {
        const td = leadRes.data.travel_date;
        // If it's a range like "15 Nov - 20 Nov 2026" or "2026-11-15"
        if (td.includes(' - ') || td.includes(' to ')) {
          const parts = td.split(/ - | to /);
          if (parts.length === 2) {
            setArrivalDate(parts[0].trim());
            setDepartureDate(parts[1].trim());
          }
        } else {
          setArrivalDate(td);
        }
      }
      if (leadRes.data?.arrival_date) setArrivalDate(leadRes.data.arrival_date);
      if (leadRes.data?.departure_date) setDepartureDate(leadRes.data.departure_date);
    } catch (err) {
      toast.error('Failed to load lead details');
      navigate('/dashboard/leads');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const changeStage = async (stage) => {
    if (stage === 'confirmed' || stage === 'lost') {
      setClosureStage(stage);
      setClosureReason('');
      setShowClosureDialog(true);
      return;
    }
    try {
      await api.put(`/leads/${id}/stage?stage=${stage}`);
      toast.success(`Stage updated to ${stage.replace('_', ' ')}`);
      fetchAll();
    } catch { toast.error('Failed to update stage'); }
  };

  const confirmClosure = async () => {
    if (!closureReason.trim()) {
      toast.error('Please provide a reason/remark');
      return;
    }
    try {
      await api.put(`/leads/${id}/stage?stage=${closureStage}&closure_reason=${encodeURIComponent(closureReason)}`);
      toast.success(`Lead marked as ${closureStage}`);
      setShowClosureDialog(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update stage');
    }
  };

  const handleGenerate = async (proceedWithoutHotel = null) => {
    if (!rawText.trim()) { toast.error('Paste your raw text or trip details first'); return; }
    const missing = [];
    if (!arrivalDate.trim()) missing.push('Arrival Date');
    if (!departureDate.trim()) missing.push('Departure Date');
    if (!destinationInput.trim()) missing.push('Destination');
    if (missing.length) { toast.error(`Required: ${missing.join(', ')}`); return; }

    setGenerating(true);
    try {
      const payload = {
        lead_id: id, raw_text: rawText, special_instructions: specialInstructions || null,
        itinerary_type: itinType, tone: itinTone,
        arrival_date: arrivalDate, departure_date: departureDate, destination: destinationInput
      };
      if (proceedWithoutHotel !== null) payload.proceed_without_hotel = proceedWithoutHotel;
      if (selectedTemplate) payload.special_instructions = `Template style: ${selectedTemplate}. ${specialInstructions || ''}`.trim();

      const res = await api.post('/itinerary/ai-format', payload);

      if (res.data.needs_confirmation && res.data.confirmation_type === 'hotel_missing') {
        setGenerating(false);
        setShowHotelConfirm(true);
        return;
      }
      if (res.data.generated && res.data.itinerary) {
        // Auto-save and open Visual Editor
        const itin = res.data.itinerary;
        const savePayload = {
          lead_id: id,
          title: itin.title || `Itinerary - ${destinationInput}`,
          days: itin.days || [],
          arrival_date: itin.arrival_date || arrivalDate,
          departure_date: itin.departure_date || departureDate,
          duration: itin.duration || '',
          total_guests: itin.total_guests || '',
          costing: itin.costing || '',
          hotel_details: itin.hotel_details || [],
          inclusions: itin.inclusions || [],
          exclusions: itin.exclusions || [],
          terms_conditions: itin.terms_conditions || [],
          notes: itin.important_notes?.join('. ') || itin.notes || '',
          status: 'draft'
        };
        const saveRes = await api.post('/itinerary/save', savePayload);
        const savedId = saveRes.data?.id;
        toast.success('Itinerary generated! Opening editor...');
        if (savedId) {
          navigate(`/itinerary-editor/${savedId}`);
        } else {
          fetchAll();
        }
      } else {
        toast.error(res.data.error || 'Generation failed');
      }
    } catch (err) {
      if (err.response?.status === 402) toast.error('Trial expired. Please subscribe.');
      else if (err.code === 'ECONNABORTED') toast.error('Timed out — please try again');
      else toast.error(err.response?.data?.detail || 'Generation failed');
    } finally { setGenerating(false); }
  };

  const handleTemplateGenerate = async (templateType) => {
    // Just toggle the template selection — don't navigate
    if (selectedTemplate === templateType) {
      setSelectedTemplate(null);
    } else {
      setSelectedTemplate(templateType);
      // Set tone based on template
      const toneMap = { honeymoon: 'luxury', family: 'professional', corporate: 'corporate', adventure: 'professional' };
      setItinTone(toneMap[templateType] || 'professional');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingFile(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await api.post('/builder/upload-document', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const sd = res.data.structured_data;
      if (sd && !sd.error) {
        if (sd.destination) setDestinationInput(sd.destination);
        if (sd.arrival_date) setArrivalDate(sd.arrival_date);
        if (sd.departure_date) setDepartureDate(sd.departure_date);
        setRawText(res.data.extracted_text || '');
        toast.success(`Extracted: ${sd.destination || 'trip data'} — ${sd.hotels?.length || 0} hotels, ${sd.activities?.length || 0} activities`);
      } else {
        setRawText(res.data.extracted_text || '');
        toast.info('Text extracted — review and generate');
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally { setUploadingFile(false); e.target.value = ''; }
  };

  const proceedToSave = (asDraft = false) => {
    if (!hotelDetails || hotelDetails.length === 0) {
      setPendingSaveAsDraft(asDraft);
      setShowHotelPopup(true);
      return;
    }
    doSaveItinerary(asDraft);
  };

  const doSaveItinerary = async (asDraft = false) => {
    if (!formattedItinerary) { toast.error('No itinerary to save'); return; }
    try {
      const pricing = manualPricing.filter(p => p.description).map(p => ({
        description: p.description, amount: p.amount
      }));

      const payload = {
        lead_id: id,
        title: formattedItinerary.title || `Itinerary - ${lead?.destination || 'Trip'}`,
        days: formattedItinerary.days || [],
        arrival_date: formattedItinerary.arrival_date || arrivalDate,
        departure_date: formattedItinerary.departure_date || departureDate,
        duration: formattedItinerary.duration || '',
        total_guests: formattedItinerary.total_guests || '',
        costing: formattedItinerary.costing || '',
        hotel_details: hotelDetails,
        inclusions: formattedItinerary.inclusions || [],
        exclusions: formattedItinerary.exclusions || [],
        terms_conditions: termsConditions,
        pricing,
        total_amount: parseFloat(itineraryTotal) || 0,
        notes: formattedItinerary.important_notes?.join('. ') || formattedItinerary.notes || '',
        status: asDraft ? 'draft' : 'final'
      };

      if (editingItineraryId) {
        await api.put(`/itinerary/${editingItineraryId}`, payload);
        toast.success('Itinerary updated');
      } else {
        await api.post('/itinerary/save', payload);
        toast.success(asDraft ? 'Draft saved' : 'Itinerary saved');
      }

      setShowItineraryEditor(false);
      setFormattedItinerary(null);
      setRawText('');
      setSpecialInstructions('');
      setManualPricing([{ description: '', amount: '' }]);
      setItineraryTotal('');
      setHotelDetails([]);
      setTermsConditions([]);
      setEditingItineraryId(null);
      fetchAll();
    } catch { toast.error('Failed to save itinerary'); }
  };

  const downloadItinerary = async (itineraryId) => {
    setDownloadingId(itineraryId);
    try {
      const token = localStorage.getItem('token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const downloadUrl = `${backendUrl}/api/itinerary/${itineraryId}/download${token ? '?token=' + encodeURIComponent(token) : ''}`;
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = downloadUrl;
      document.body.appendChild(iframe);
      setTimeout(() => document.body.removeChild(iframe), 30000);
      toast.success('Download started');
    } catch { toast.error('Download failed'); }
    finally { setTimeout(() => setDownloadingId(null), 2000); }
  };

  const duplicateItinerary = async (itineraryId) => {
    try {
      await api.post(`/itinerary/${itineraryId}/duplicate`);
      toast.success('Itinerary duplicated');
      fetchAll();
    } catch { toast.error('Failed to duplicate'); }
  };

  const editItinerary = (itin) => {
    setFormattedItinerary({
      title: itin.title,
      days: itin.days || [],
      arrival_date: itin.arrival_date || itin.arrival_details || '',
      departure_date: itin.departure_date || itin.departure_details || '',
      duration: itin.duration || '',
      total_guests: itin.total_guests || '',
      costing: itin.costing || '',
      inclusions: itin.inclusions || [],
      exclusions: itin.exclusions || [],
      important_notes: itin.notes ? [itin.notes] : [],
      notes: itin.notes || ''
    });
    setHotelDetails(itin.hotel_details || []);
    setTermsConditions(itin.terms_conditions || []);
    setManualPricing(itin.pricing?.length ? itin.pricing.map(p => ({ description: p.description || '', amount: p.amount || '' })) : [{ description: '', amount: '' }]);
    setItineraryTotal(itin.total_amount ? String(itin.total_amount) : '');
    setArrivalDate(itin.arrival_date || itin.arrival_details || '');
    setDepartureDate(itin.departure_date || itin.departure_details || '');
    setEditingItineraryId(itin.id);
    setShowItineraryEditor(true);
  };

  const addQuotation = async () => {
    const total = newQuotation.items.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
    try {
      await api.post('/quotations', { lead_id: id, items: newQuotation.items, total_amount: total, currency: newQuotation.currency, notes: newQuotation.notes });
      toast.success('Quotation created');
      setShowQuotation(false);
      setNewQuotation({ items: [{ description: '', type: 'hotel', amount: 0 }], total_amount: 0, currency: 'INR', notes: '' });
      fetchAll();
    } catch { toast.error('Failed to create quotation'); }
  };

  const removeQuotationItem = (idx) => {
    const items = newQuotation.items.filter((_, i) => i !== idx);
    setNewQuotation({ ...newQuotation, items: items.length ? items : [{ description: '', type: 'service', amount: 0 }] });
  };

  const addFollowup = async () => {
    if (!newFollowup.scheduled_at) { toast.error('Select date/time'); return; }
    try {
      await api.post('/followups', { lead_id: id, ...newFollowup });
      toast.success('Follow-up scheduled');
      setShowFollowup(false);
      setNewFollowup({ type: 'call', scheduled_at: '', notes: '' });
      fetchAll();
    } catch { toast.error('Failed to schedule follow-up'); }
  };

  const completeFollowup = async (fid) => {
    try {
      await api.put(`/followups/${fid}`, { status: 'completed', completed_at: new Date().toISOString() });
      toast.success('Follow-up completed');
      fetchAll();
    } catch {}
  };

  const addPayment = async () => {
    if (!newPayment.amount) { toast.error('Enter amount'); return; }
    try {
      await api.post('/payments', { lead_id: id, ...newPayment });
      toast.success('Payment recorded');
      setShowPayment(false);
      setNewPayment({ amount: 0, payment_method: 'bank_transfer', reference: '', notes: '' });
      fetchAll();
    } catch { toast.error('Failed to record payment'); }
  };

  const addHotelRow = () => setHotelDetails([...hotelDetails, { place: '', date: '', hotel_name: '', room_type: '', meal_plan: '' }]);
  const updateHotelRow = (idx, field, value) => {
    const updated = [...hotelDetails];
    updated[idx] = { ...updated[idx], [field]: value };
    setHotelDetails(updated);
  };
  const removeHotelRow = (idx) => setHotelDetails(hotelDetails.filter((_, i) => i !== idx));

  const updateDay = (idx, field, value) => {
    const days = [...(formattedItinerary?.days || [])];
    days[idx] = { ...days[idx], [field]: value };
    setFormattedItinerary({ ...formattedItinerary, days });
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;
  if (!lead) return null;

  const totalQuoted = quotations.reduce((s, q) => s + (q.total_amount || 0), 0);
  const totalPaid = payments.filter(p => p.status === 'received').reduce((s, p) => s + (p.amount || 0), 0);
  const balance = totalQuoted - totalPaid;
  const currentStageConf = STAGE_CONFIG[lead.stage] || STAGE_CONFIG.new;

  return (
    <div className="space-y-5 animate-fade-in" data-testid="lead-detail-page">
      {/* Back button */}
      <Button variant="ghost" onClick={() => navigate('/dashboard/leads')} className="gap-2 text-muted-foreground hover:text-foreground" data-testid="back-to-leads-btn">
        <ArrowLeft className="w-4 h-4" /> Back to Leads
      </Button>

      {/* ── BENTO CONTROL ROOM CARD ────────────────── */}
      <Card className="border border-border bento-card overflow-hidden" data-testid="lead-bento-card">
        <CardContent className="p-6">
          {/* Top row: name, inquiry ID, badges */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-5">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="font-heading text-2xl font-bold text-foreground tracking-tight" data-testid="lead-detail-name">{lead.client_name || 'Unknown'}</h1>
              <span className="text-xs text-muted-foreground font-mono bg-muted px-2 py-0.5 rounded" data-testid="lead-detail-inquiry-id">{lead.inquiry_id}</span>
              {lead.department && (
                <Badge className="rounded-full px-2.5 py-0.5 text-[10px] font-medium bg-cyan-50 text-cyan-700 border-cyan-200 dark:bg-cyan-900/30 dark:text-cyan-300 capitalize">
                  {lead.department}
                </Badge>
              )}
              {lead.is_closed && <Badge className="bg-slate-500 text-white text-xs rounded-full">Closed</Badge>}
            </div>
            <Badge className={`rounded-full px-3 py-1 text-xs font-semibold ${currentStageConf.lightBg} ${currentStageConf.text} border`} data-testid="lead-detail-stage-badge">
              {currentStageConf.label}
            </Badge>
          </div>

          {/* Bento grid — all key data at a glance */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <div data-testid="lead-detail-phone">
              <p className="text-[10px] text-muted-foreground uppercase tracking-[0.2em] font-medium mb-1">Phone</p>
              <div className="flex items-center gap-1.5">
                <Phone className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                <p className="text-sm font-medium text-foreground truncate">{lead.phone || 'N/A'}</p>
              </div>
            </div>
            <div data-testid="lead-detail-email">
              <p className="text-[10px] text-muted-foreground uppercase tracking-[0.2em] font-medium mb-1">Email</p>
              <div className="flex items-center gap-1.5">
                <Mail className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
                <p className="text-sm font-medium text-foreground truncate">{lead.email || 'N/A'}</p>
              </div>
            </div>
            <div data-testid="lead-detail-destination">
              <p className="text-[10px] text-muted-foreground uppercase tracking-[0.2em] font-medium mb-1">Destination</p>
              <div className="flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5 text-voyage-blue flex-shrink-0" />
                <p className="text-sm font-medium text-foreground truncate">{lead.destination || 'N/A'}</p>
              </div>
            </div>
            <div data-testid="lead-detail-budget">
              <p className="text-[10px] text-muted-foreground uppercase tracking-[0.2em] font-medium mb-1">Budget</p>
              <div className="flex items-center gap-1.5">
                <IndianRupee className="w-3.5 h-3.5 text-voyage-orange flex-shrink-0" />
                <p className="text-sm font-medium text-foreground truncate">{lead.budget || 'N/A'}</p>
              </div>
            </div>
            <div data-testid="lead-detail-dates">
              <p className="text-[10px] text-muted-foreground uppercase tracking-[0.2em] font-medium mb-1">Travel Date</p>
              <div className="flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5 text-violet-500 flex-shrink-0" />
                <p className="text-sm font-medium text-foreground truncate">{lead.travel_date || 'N/A'}</p>
              </div>
            </div>
            <div data-testid="lead-detail-guests">
              <p className="text-[10px] text-muted-foreground uppercase tracking-[0.2em] font-medium mb-1">Guests</p>
              <div className="flex items-center gap-1.5">
                <Users className="w-3.5 h-3.5 text-cyan-500 flex-shrink-0" />
                <p className="text-sm font-medium text-foreground">{lead.adults || 0}A, {lead.children || 0}C</p>
              </div>
            </div>
          </div>

          {/* Extra info row */}
          <div className="flex items-center gap-4 mt-4 pt-4 border-t border-border/50 text-xs text-muted-foreground flex-wrap">
            <span className="flex items-center gap-1"><Briefcase className="w-3 h-3" /> {lead.booking_type || 'N/A'}</span>
            <span className="flex items-center gap-1"><Tag className="w-3 h-3" /> {lead.source}</span>
            <span className="flex items-center gap-1 capitalize"><span className={`w-1.5 h-1.5 rounded-full ${lead.priority === 'urgent' ? 'bg-red-500' : lead.priority === 'high' ? 'bg-orange-500' : lead.priority === 'medium' ? 'bg-blue-500' : 'bg-slate-400'}`} /> {lead.priority}</span>
            <span className="flex items-center gap-1"><CalendarDays className="w-3 h-3" /> Created: {new Date(lead.created_at).toLocaleDateString()}</span>
            {lead.closure_reason && <span className="italic text-muted-foreground">Closure: {lead.closure_reason}</span>}
          </div>
        </CardContent>
      </Card>

      {/* ── PIPELINE STAGE PILLS ────────────────── */}
      <Card className="border border-border bg-card" data-testid="stage-pipeline">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 overflow-x-auto pb-1">
            {STAGES.map((stage, i) => {
              const conf = STAGE_CONFIG[stage];
              const isActive = lead.stage === stage;
              const isPast = STAGES.indexOf(lead.stage) > i;
              return (
                <button key={stage} onClick={() => changeStage(stage)}
                  className={`flex-shrink-0 px-4 py-2 rounded-full text-xs font-semibold transition-all duration-200 border ${
                    isActive
                      ? `${conf.bg} text-white border-transparent shadow-md stage-active`
                      : isPast
                        ? `${conf.lightBg} ${conf.text} border-transparent`
                        : 'bg-muted/50 text-muted-foreground border-border hover:bg-muted hover:border-muted-foreground/20'
                  }`}
                  data-testid={`stage-btn-${stage}`}>
                  {conf.label}
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Closure Reason Dialog */}
      <Dialog open={showClosureDialog} onOpenChange={setShowClosureDialog}>
        <DialogContent data-testid="closure-reason-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {closureStage === 'confirmed' ? 'Confirm Booking' : 'Mark as Lost'}
            </DialogTitle>
            <DialogDescription>
              Please provide a mandatory reason/remark before {closureStage === 'confirmed' ? 'confirming' : 'closing'} this lead.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Textarea
              placeholder={closureStage === 'confirmed' ? 'e.g., Payment received, booking confirmed by client...' : 'e.g., Client chose competitor, budget issues...'}
              value={closureReason}
              onChange={(e) => setClosureReason(e.target.value)}
              rows={3}
              data-testid="closure-reason-input"
            />
            <div className="flex gap-2">
              <Button onClick={confirmClosure} className={`flex-1 text-white ${closureStage === 'confirmed' ? 'gradient-emerald' : 'bg-red-500 hover:bg-red-600'}`} data-testid="confirm-closure-btn">
                {closureStage === 'confirmed' ? 'Confirm Booking' : 'Mark as Lost'}
              </Button>
              <Button variant="outline" onClick={() => setShowClosureDialog(false)}>Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── FINANCIAL SUMMARY ────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Card className="border border-border card-hover-lift stat-card-violet">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-violet-500/15 flex items-center justify-center"><FileText className="w-5 h-5 text-violet-500" /></div>
            <div><p className="text-[10px] text-muted-foreground uppercase tracking-[0.15em] font-medium">Quoted</p><p className="font-heading font-bold text-lg">INR {totalQuoted.toLocaleString()}</p></div>
          </CardContent>
        </Card>
        <Card className="border border-border card-hover-lift stat-card-emerald">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/15 flex items-center justify-center"><CreditCard className="w-5 h-5 text-emerald-500" /></div>
            <div><p className="text-[10px] text-muted-foreground uppercase tracking-[0.15em] font-medium">Paid</p><p className="font-heading font-bold text-lg text-emerald-600">INR {totalPaid.toLocaleString()}</p></div>
          </CardContent>
        </Card>
        <Card className="border border-border card-hover-lift stat-card-orange">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-orange-500/15 flex items-center justify-center"><IndianRupee className="w-5 h-5 text-orange-500" /></div>
            <div><p className="text-[10px] text-muted-foreground uppercase tracking-[0.15em] font-medium">Balance</p><p className="font-heading font-bold text-lg text-orange-600">INR {balance.toLocaleString()}</p></div>
          </CardContent>
        </Card>
      </div>

      {/* ── TABS (4 tabs — Details tab removed, merged into Bento Card) ── */}
      <Tabs defaultValue="itinerary" className="space-y-4">
        <TabsList className="bg-muted/60 h-auto p-1 rounded-xl" data-testid="lead-tabs">
          <TabsTrigger value="itinerary" className="text-sm px-5 py-2.5 rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm dark:data-[state=active]:bg-card">
            Itinerary ({itineraries.length})
          </TabsTrigger>
          <TabsTrigger value="quotations" className="text-sm px-5 py-2.5 rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm dark:data-[state=active]:bg-card">
            Pricing ({quotations.length})
          </TabsTrigger>
          <TabsTrigger value="followups" className="text-sm px-5 py-2.5 rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm dark:data-[state=active]:bg-card">
            Follow-ups ({followups.length})
          </TabsTrigger>
          <TabsTrigger value="payments" className="text-sm px-5 py-2.5 rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm dark:data-[state=active]:bg-card">
            Payments ({payments.length})
          </TabsTrigger>
        </TabsList>

        {/* ── Itinerary Tab ── */}
        <TabsContent value="itinerary" className="space-y-4">

          {/* AI Itinerary Builder — Redesigned */}
          {!showItineraryEditor && (
            <div className="space-y-4" data-testid="itinerary-builder">
              {/* Hero Header */}
              <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 p-6 md:p-8">
                <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAwIDEwIEwgNDAgMTAgTSAxMCAwIEwgMTAgNDAiIGZpbGw9Im5vbmUiIHN0cm9rZT0icmdiYSgyNTUsMjU1LDI1NSwwLjAzKSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCBmaWxsPSJ1cmwoI2dyaWQpIiB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIi8+PC9zdmc+')] opacity-50" />
                <div className="relative z-10">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-xl bg-white/10 backdrop-blur flex items-center justify-center"><Sparkles className="w-5 h-5 text-amber-400" /></div>
                    <div>
                      <h3 className="font-heading text-lg font-bold text-white">AI Itinerary Builder</h3>
                      <p className="text-xs text-blue-200">Create stunning client-ready itineraries in seconds</p>
                    </div>
                  </div>
                  {/* Quick Templates */}
                  <div className="flex flex-wrap gap-2 mt-4">
                    {[
                      { id: 'honeymoon', label: 'Honeymoon', icon: Heart, color: 'from-pink-500 to-rose-500', ring: 'ring-pink-400' },
                      { id: 'family', label: 'Family', icon: Users, color: 'from-sky-500 to-blue-500', ring: 'ring-sky-400' },
                      { id: 'corporate', label: 'Corporate', icon: Building2, color: 'from-slate-500 to-gray-600', ring: 'ring-slate-400' },
                      { id: 'adventure', label: 'Adventure', icon: Mountain, color: 'from-emerald-500 to-green-600', ring: 'ring-emerald-400' },
                    ].map(t => (
                      <button key={t.id} onClick={() => handleTemplateGenerate(t.id)}
                        className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-medium text-white bg-gradient-to-r ${t.color} transition-all shadow-sm ${
                          selectedTemplate === t.id ? `ring-2 ${t.ring} ring-offset-2 ring-offset-slate-900 scale-105` : 'opacity-80 hover:opacity-100'
                        }`}
                        data-testid={`template-${t.id}`}>
                        <t.icon className="w-3 h-3" />
                        {t.label}
                        {selectedTemplate === t.id && <Check className="w-3 h-3 ml-0.5" />}
                      </button>
                    ))}
                    {selectedTemplate && (
                      <button onClick={() => { setSelectedTemplate(null); setItinTone('professional'); }}
                        className="flex items-center gap-1 px-2 py-1 rounded-full text-[10px] text-white/60 hover:text-white/90 transition-colors">
                        <X className="w-3 h-3" /> Clear
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Builder Form Card */}
              <Card className="border border-border shadow-sm">
                <CardContent className="p-5 space-y-4">
                  {/* Row 1: Destination + Dates */}
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                    <div className="md:col-span-2 space-y-1.5">
                      <Label className="text-xs font-semibold text-slate-600 dark:text-slate-400 flex items-center gap-1.5">
                        <MapPin className="w-3.5 h-3.5 text-blue-500" /> Destination
                      </Label>
                      <Input value={destinationInput} onChange={e => setDestinationInput(e.target.value)}
                        placeholder="Bali, Maldives, Switzerland..." className="h-10 text-sm font-medium border-slate-200 focus:border-blue-400" data-testid="itinerary-destination" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold text-slate-600 dark:text-slate-400 flex items-center gap-1.5">
                        <PlaneTakeoff className="w-3.5 h-3.5 text-emerald-500" /> Arrival
                      </Label>
                      <Input type="date" value={arrivalDate} onChange={e => setArrivalDate(e.target.value)} className="h-10 text-sm" data-testid="itinerary-arrival-date" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold text-slate-600 dark:text-slate-400 flex items-center gap-1.5">
                        <PlaneLanding className="w-3.5 h-3.5 text-rose-500" /> Departure
                      </Label>
                      <Input type="date" value={departureDate} onChange={e => setDepartureDate(e.target.value)} className="h-10 text-sm" data-testid="itinerary-departure-date" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold text-slate-600 dark:text-slate-400 flex items-center gap-1.5">
                        <Globe className="w-3.5 h-3.5 text-violet-500" /> Currency
                      </Label>
                      <Select value={currency} onValueChange={setCurrency}>
                        <SelectTrigger className="h-10 text-sm"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="INR">INR (₹)</SelectItem>
                          <SelectItem value="USD">USD ($)</SelectItem>
                          <SelectItem value="EUR">EUR (€)</SelectItem>
                          <SelectItem value="GBP">GBP (£)</SelectItem>
                          <SelectItem value="AED">AED (د.إ)</SelectItem>
                          <SelectItem value="THB">THB (฿)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Row 2: Type + Tone */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold text-slate-600 dark:text-slate-400">Itinerary Type</Label>
                      <Select value={itinType} onValueChange={setItinType}>
                        <SelectTrigger className="h-10 text-sm" data-testid="itinerary-type-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="detailed">Detailed Itinerary</SelectItem>
                          <SelectItem value="brief">Brief Itinerary</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold text-slate-600 dark:text-slate-400">Tone</Label>
                      <Select value={itinTone} onValueChange={setItinTone}>
                        <SelectTrigger className="h-10 text-sm" data-testid="itinerary-tone-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="luxury">Luxury</SelectItem>
                          <SelectItem value="budget">Budget Friendly</SelectItem>
                          <SelectItem value="corporate">Corporate</SelectItem>
                          <SelectItem value="professional">Professional</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Raw Text Area */}
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs font-semibold text-slate-600 dark:text-slate-400">Trip Details / DMC Data</Label>
                      <label className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-700 cursor-pointer font-medium">
                        {uploadingFile ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                        {uploadingFile ? 'Extracting...' : 'Upload PDF/Word/Excel'}
                        <input type="file" accept=".pdf,.docx,.xlsx,.xls,.txt" onChange={handleFileUpload} className="hidden" data-testid="file-upload-input" />
                      </label>
                    </div>
                    <Textarea value={rawText} onChange={e => setRawText(e.target.value)}
                      placeholder={"Paste supplier quotation, DMC data, or describe the trip:\n\n• 4 nights Ubud + 3 nights Seminyak\n• Transfers, sightseeing, water rafting\n• Hotels: Maya Ubud Resort, W Bali Seminyak"}
                      rows={5} className="text-sm border-slate-200 focus:border-blue-400 resize-none" data-testid="itinerary-raw-text" />
                  </div>

                  <Input value={specialInstructions} onChange={e => setSpecialInstructions(e.target.value)}
                    placeholder="Special instructions: e.g., include shopping time, add spa sessions, romantic touches..."
                    className="h-10 text-sm border-slate-200 focus:border-blue-400" data-testid="itinerary-instructions" />

                  {/* Generate Button */}
                  <Button onClick={() => handleGenerate()} disabled={generating}
                    className="w-full h-11 gap-2 text-sm font-semibold bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-lg shadow-blue-500/20"
                    data-testid="format-itinerary-btn">
                    {generating ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating your itinerary...</> : <><Sparkles className="w-4 h-4" /> {selectedTemplate ? `Generate ${selectedTemplate.charAt(0).toUpperCase() + selectedTemplate.slice(1)} Itinerary` : 'Generate AI Itinerary'}</>}
                  </Button>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Hotel Not Found Confirmation */}
          <Dialog open={showHotelConfirm} onOpenChange={setShowHotelConfirm}>
            <DialogContent data-testid="hotel-missing-popup">
              <DialogHeader>
                <DialogTitle className="font-heading flex items-center gap-2">
                  <Hotel className="w-5 h-5 text-amber-500" /> Hotel Details Not Found
                </DialogTitle>
                <DialogDescription>
                  No hotel details were found in the pasted content. Would you like to proceed without hotel details, or go back and add them?
                </DialogDescription>
              </DialogHeader>
              <div className="flex gap-2">
                <Button onClick={() => { setShowHotelConfirm(false); handleGenerate(true); }}
                  className="flex-1 gradient-blue hover:opacity-90 text-white" data-testid="proceed-without-hotel-btn">
                  Proceed Without Hotels
                </Button>
                <Button variant="outline" onClick={() => setShowHotelConfirm(false)} data-testid="go-back-add-hotel-btn">
                  Go Back & Add Hotels
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Itinerary Editor */}
          {showItineraryEditor && formattedItinerary && (
            <Card className="border-2 border-primary/20 bg-card" data-testid="itinerary-editor">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="font-heading text-base flex items-center gap-2">
                    <Edit3 className="w-4 h-4 text-primary" />
                    {editingItineraryId ? 'Edit Itinerary' : 'Formatted Itinerary'}
                  </CardTitle>
                  <Badge variant="outline" className="text-xs">{editingItineraryId ? 'Editing' : 'Preview & Edit'}</Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <Input value={formattedItinerary.title || ''} onChange={(e) => setFormattedItinerary({...formattedItinerary, title: e.target.value})}
                  className="font-heading font-bold text-lg" data-testid="itinerary-title-input" placeholder="Itinerary Title" />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs font-medium flex items-center gap-1"><PlaneTakeoff className="w-3 h-3" /> Arrival Date</Label>
                    <div className="flex gap-1.5">
                      <Input type="date"
                        value={String(formattedItinerary.arrival_date || '').match(/^\d{4}-\d{2}-\d{2}/) ? String(formattedItinerary.arrival_date).split(' ')[0] : ''}
                        onChange={(e) => setFormattedItinerary({...formattedItinerary, arrival_date: e.target.value})}
                        className="text-sm h-9 flex-1" data-testid="editor-arrival-date" />
                      <Input value={formattedItinerary.arrival_date || ''}
                        onChange={(e) => setFormattedItinerary({...formattedItinerary, arrival_date: e.target.value})}
                        placeholder="e.g., 15 Jan 2026" className="text-sm h-9 flex-1" data-testid="editor-arrival-text" />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs font-medium flex items-center gap-1"><PlaneLanding className="w-3 h-3" /> Departure Date</Label>
                    <div className="flex gap-1.5">
                      <Input type="date"
                        value={String(formattedItinerary.departure_date || '').match(/^\d{4}-\d{2}-\d{2}/) ? String(formattedItinerary.departure_date).split(' ')[0] : ''}
                        onChange={(e) => setFormattedItinerary({...formattedItinerary, departure_date: e.target.value})}
                        className="text-sm h-9 flex-1" data-testid="editor-departure-date" />
                      <Input value={formattedItinerary.departure_date || ''}
                        onChange={(e) => setFormattedItinerary({...formattedItinerary, departure_date: e.target.value})}
                        placeholder="e.g., 20 Jan 2026" className="text-sm h-9 flex-1" data-testid="editor-departure-text" />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs font-medium">Duration</Label>
                    <Input value={formattedItinerary.duration || ''}
                      onChange={(e) => setFormattedItinerary({...formattedItinerary, duration: e.target.value})}
                      placeholder="e.g., 4 Nights / 5 Days" className="text-sm h-9" data-testid="editor-duration" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs font-medium">Total Guests</Label>
                    <Input value={formattedItinerary.total_guests || ''}
                      onChange={(e) => setFormattedItinerary({...formattedItinerary, total_guests: e.target.value})}
                      placeholder="e.g., 2 Adults, 1 Child" className="text-sm h-9" data-testid="editor-total-guests" />
                  </div>
                </div>

                {/* Hotel Details Table */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-semibold flex items-center gap-1"><Hotel className="w-4 h-4 text-voyage-blue" /> Hotel Details</Label>
                    <Button variant="outline" size="sm" onClick={addHotelRow} className="text-xs gap-1" data-testid="add-hotel-row-btn">
                      <Plus className="w-3 h-3" /> Add Hotel
                    </Button>
                  </div>
                  {hotelDetails.length > 0 ? (
                    <div className="border rounded-lg overflow-x-auto">
                      <Table data-testid="hotel-details-table">
                        <TableHeader>
                          <TableRow>
                            <TableHead className="text-xs">Place</TableHead>
                            <TableHead className="text-xs">Date</TableHead>
                            <TableHead className="text-xs">Hotel Name</TableHead>
                            <TableHead className="text-xs">Room Type</TableHead>
                            <TableHead className="text-xs">Meal Plan</TableHead>
                            <TableHead className="w-8"></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {hotelDetails.map((h, idx) => (
                            <TableRow key={`hotel-${idx}-${h.hotel_name || ''}`}>
                              <TableCell><Input value={h.place} onChange={e => updateHotelRow(idx, 'place', e.target.value)} className="text-xs h-7 w-28" /></TableCell>
                              <TableCell><Input value={h.date} onChange={e => updateHotelRow(idx, 'date', e.target.value)} className="text-xs h-7 w-28" /></TableCell>
                              <TableCell><Input value={h.hotel_name} onChange={e => updateHotelRow(idx, 'hotel_name', e.target.value)} className="text-xs h-7 w-32" /></TableCell>
                              <TableCell><Input value={h.room_type} onChange={e => updateHotelRow(idx, 'room_type', e.target.value)} className="text-xs h-7 w-24" /></TableCell>
                              <TableCell><Input value={h.meal_plan} onChange={e => updateHotelRow(idx, 'meal_plan', e.target.value)} className="text-xs h-7 w-20" /></TableCell>
                              <TableCell>
                                <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive" onClick={() => removeHotelRow(idx)}>
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground italic">No hotels added yet. Click "Add Hotel" or proceed without.</p>
                  )}
                </div>

                <Separator />

                {/* Editable Days */}
                <div className="space-y-2">
                  <Label className="text-sm font-semibold">Day-wise Itinerary</Label>
                  {formattedItinerary.days?.map((day, i) => (
                    <div key={`edit-day-${day.day}`} className="p-3 rounded-lg bg-muted/30 border border-border/50 space-y-2">
                      <div className="flex items-center gap-2">
                        <Badge className="bg-voyage-blue/15 text-voyage-blue text-xs font-semibold border-0">Day {day.day}</Badge>
                        <Input value={day.title || ''} onChange={e => updateDay(i, 'title', e.target.value)}
                          className="text-sm h-7 font-medium flex-1" placeholder="Day title" />
                      </div>
                      <Textarea value={day.description || ''} onChange={e => updateDay(i, 'description', e.target.value)}
                        className="text-sm" rows={2} placeholder="Day description" />
                      <div className="grid grid-cols-2 gap-2">
                        <Input value={day.hotel || ''} onChange={e => updateDay(i, 'hotel', e.target.value)}
                          className="text-xs h-7" placeholder="Hotel name" />
                        <Input value={day.meals || ''} onChange={e => updateDay(i, 'meals', e.target.value)}
                          className="text-xs h-7" placeholder="Meals (e.g., Breakfast, Dinner)" />
                      </div>
                    </div>
                  ))}
                </div>

                {formattedItinerary.costing && (
                  <div className="space-y-1">
                    <Label className="text-xs font-semibold">Costing (from input)</Label>
                    <Textarea value={formattedItinerary.costing || ''}
                      onChange={(e) => setFormattedItinerary({...formattedItinerary, costing: e.target.value})}
                      rows={3} className="text-xs" placeholder="Costing details as provided" data-testid="editor-costing" />
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs font-semibold text-emerald-600">Inclusions</Label>
                    <Textarea value={(formattedItinerary.inclusions || []).join('\n')}
                      onChange={(e) => setFormattedItinerary({...formattedItinerary, inclusions: e.target.value.split('\n').filter(Boolean)})}
                      rows={4} className="text-xs" placeholder="One per line" data-testid="editor-inclusions" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs font-semibold text-red-500">Exclusions</Label>
                    <Textarea value={(formattedItinerary.exclusions || []).join('\n')}
                      onChange={(e) => setFormattedItinerary({...formattedItinerary, exclusions: e.target.value.split('\n').filter(Boolean)})}
                      rows={4} className="text-xs" placeholder="One per line" data-testid="editor-exclusions" />
                  </div>
                </div>

                <div className="space-y-1">
                  <Label className="text-xs font-semibold">Terms & Conditions</Label>
                  <Textarea value={termsConditions.join('\n')}
                    onChange={(e) => setTermsConditions(e.target.value.split('\n').filter(Boolean))}
                    rows={3} className="text-xs" placeholder="One per line" data-testid="editor-terms" />
                </div>

                <Separator />

                {/* Manual Pricing */}
                <div>
                  <p className="font-heading font-semibold text-sm mb-2">Manual Pricing</p>
                  {manualPricing.map((item, idx) => (
                    <div key={`pricing-${idx}`} className="flex gap-2 mb-2 items-center">
                      <Input placeholder="Description" value={item.description}
                        onChange={e => { const p = [...manualPricing]; p[idx].description = e.target.value; setManualPricing(p); }}
                        className="flex-1 text-sm" />
                      <Input placeholder="Amount" value={item.amount} type="text"
                        onChange={e => { const p = [...manualPricing]; p[idx].amount = e.target.value; setManualPricing(p); }}
                        className="w-32 text-sm" />
                      {manualPricing.length > 1 && (
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => setManualPricing(manualPricing.filter((_, i) => i !== idx))}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  ))}
                  <div className="flex items-center justify-between">
                    <Button variant="outline" size="sm" onClick={() => setManualPricing([...manualPricing, { description: '', amount: '' }])}>+ Add Item</Button>
                    <div className="flex items-center gap-2">
                      <Label className="text-sm font-semibold">Total:</Label>
                      <Input placeholder="Total amount" value={itineraryTotal} onChange={(e) => setItineraryTotal(e.target.value)} className="w-32 text-sm font-bold" data-testid="itinerary-total-input" />
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-2">
                  <Button onClick={() => proceedToSave(false)} className="flex-1 gradient-blue hover:opacity-90 text-white gap-2 shadow-md" data-testid="save-itinerary-btn">
                    <Save className="w-4 h-4" /> {editingItineraryId ? 'Update Itinerary' : 'Save Itinerary'}
                  </Button>
                  <Button onClick={() => proceedToSave(true)} variant="outline" className="gap-2" data-testid="save-draft-btn">
                    <FileText className="w-4 h-4" /> Save as Draft
                  </Button>
                  <Button variant="outline" onClick={() => { setShowItineraryEditor(false); setFormattedItinerary(null); setEditingItineraryId(null); }}>Cancel</Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Hotel missing popup */}
          <Dialog open={showHotelPopup} onOpenChange={setShowHotelPopup}>
            <DialogContent data-testid="hotel-missing-popup">
              <DialogHeader>
                <DialogTitle className="font-heading flex items-center gap-2">
                  <Hotel className="w-5 h-5 text-amber-500" /> Hotel Not Added
                </DialogTitle>
                <DialogDescription>
                  No hotel details have been added to this itinerary. Do you want to proceed without hotel details?
                </DialogDescription>
              </DialogHeader>
              <div className="flex gap-2">
                <Button onClick={() => { setShowHotelPopup(false); doSaveItinerary(pendingSaveAsDraft); }}
                  className="flex-1 gradient-blue hover:opacity-90 text-white" data-testid="proceed-without-hotel-btn">
                  Proceed Without Hotel
                </Button>
                <Button variant="outline" onClick={() => setShowHotelPopup(false)} data-testid="go-back-add-hotel-btn">
                  Go Back & Add Hotel
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Saved Itineraries */}
          {itineraries.map(itin => (
            <Card key={itin.id} className="border border-border card-hover-lift" data-testid={`itinerary-${itin.id}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-heading font-semibold">{itin.title}</p>
                      <Badge className={`rounded-full text-[10px] border ${itin.status === 'draft' ? 'text-amber-600 bg-amber-50 border-amber-200' : 'text-emerald-600 bg-emerald-50 border-emerald-200'}`}>
                        {itin.status === 'draft' ? 'Draft' : `v${itin.version}`}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground">{new Date(itin.created_at).toLocaleDateString()}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button size="sm" variant="ghost" className="gap-1 h-7 text-xs hover:text-voyage-blue" onClick={() => editItinerary(itin)} data-testid={`edit-itinerary-${itin.id}`}>
                      <Edit3 className="w-3 h-3" /> Edit
                    </Button>
                    <Button size="sm" variant="ghost" className="gap-1 h-7 text-xs hover:text-emerald-600" onClick={() => navigate(`/itinerary-editor/${itin.id}`)} data-testid={`open-editor-${itin.id}`}>
                      <Palette className="w-3 h-3" /> Visual Editor
                    </Button>
                    <Button size="sm" variant="ghost" className="gap-1 h-7 text-xs hover:text-violet-600" onClick={() => duplicateItinerary(itin.id)} data-testid={`duplicate-itinerary-${itin.id}`}>
                      <Copy className="w-3 h-3" /> Duplicate
                    </Button>
                    <Button size="sm" variant="outline" className="gap-1 h-7 text-xs" onClick={() => downloadItinerary(itin.id)}
                      disabled={downloadingId === itin.id} data-testid={`download-itinerary-${itin.id}`}>
                      {downloadingId === itin.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileDown className="w-3 h-3" />}
                      Word
                    </Button>
                  </div>
                </div>

                {itin.hotel_details?.length > 0 && (
                  <div className="mb-2 p-2 rounded-lg bg-voyage-blue/5 border border-voyage-blue/10">
                    <p className="text-xs font-semibold mb-1 flex items-center gap-1 text-voyage-blue"><Hotel className="w-3 h-3" /> Hotels</p>
                    {itin.hotel_details.map((h, i) => (
                      <p key={`${h.hotel_name}-${h.place}-${i}`} className="text-xs text-muted-foreground">{h.place}: {h.hotel_name} ({h.room_type}, {h.meal_plan})</p>
                    ))}
                  </div>
                )}

                {itin.days?.slice(0, 3).map((day, i) => (
                  <div key={`saved-day-${day.day}-${i}`} className="py-1.5 border-b border-border/30 last:border-0">
                    <p className="text-sm"><span className="font-medium text-voyage-blue">Day {day.day}:</span> {day.title}</p>
                    <p className="text-xs text-muted-foreground truncate">{day.description}</p>
                  </div>
                ))}
                {itin.days?.length > 3 && <p className="text-xs text-muted-foreground mt-1">+ {itin.days.length - 3} more days</p>}
                {itin.total_amount > 0 && (
                  <div className="mt-2 pt-2 border-t border-border/50 flex justify-between">
                    <span className="text-sm text-muted-foreground">Total</span>
                    <span className="font-heading font-bold text-voyage-blue">INR {itin.total_amount.toLocaleString()}</span>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        {/* ── Quotations/Pricing Tab ── */}
        <TabsContent value="quotations" className="space-y-3">
          <Dialog open={showQuotation} onOpenChange={setShowQuotation}>
            <DialogTrigger asChild>
              <Button className="gap-2 gradient-blue hover:opacity-90 text-white shadow-md" data-testid="add-quotation-btn"><Plus className="w-4 h-4" /> Add Pricing</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-heading">Add Manual Pricing</DialogTitle><DialogDescription>Add pricing line items manually.</DialogDescription></DialogHeader>
              <div className="space-y-3">
                {newQuotation.items.map((item, idx) => (
                  <div key={`quote-item-${idx}`} className="flex gap-2 items-start">
                    <div className="flex-1 grid grid-cols-4 gap-2">
                      <Input placeholder="Description" value={item.description} onChange={e => { const items = [...newQuotation.items]; items[idx].description = e.target.value; setNewQuotation({...newQuotation, items}); }} className="col-span-2 text-sm" />
                      <Select value={item.type || 'service'} onValueChange={v => { const items = [...newQuotation.items]; items[idx].type = v; setNewQuotation({...newQuotation, items}); }}>
                        <SelectTrigger className="text-sm"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="hotel">Hotel</SelectItem><SelectItem value="flight">Flight</SelectItem>
                          <SelectItem value="visa">Visa</SelectItem><SelectItem value="transfer">Transfer</SelectItem>
                          <SelectItem value="activity">Activity</SelectItem><SelectItem value="insurance">Insurance</SelectItem>
                          <SelectItem value="ticket">Ticket</SelectItem><SelectItem value="service_fee">Service Fee</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input type="number" placeholder="Amount" value={item.amount} onChange={e => { const items = [...newQuotation.items]; items[idx].amount = parseFloat(e.target.value) || 0; setNewQuotation({...newQuotation, items}); }} className="text-sm" />
                    </div>
                    <Button variant="ghost" size="icon" className="h-9 w-9 text-destructive" onClick={() => removeQuotationItem(idx)}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                ))}
                <div className="flex items-center justify-between">
                  <Button variant="outline" size="sm" onClick={() => setNewQuotation({...newQuotation, items: [...newQuotation.items, { description: '', type: 'service_fee', amount: 0 }]})}>+ Add Item</Button>
                  <span className="font-heading font-bold text-sm">Total: INR {newQuotation.items.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0).toLocaleString()}</span>
                </div>
                <Textarea placeholder="Notes" value={newQuotation.notes} onChange={e => setNewQuotation({...newQuotation, notes: e.target.value})} rows={2} />
                <Button onClick={addQuotation} className="w-full gradient-blue hover:opacity-90 text-white" data-testid="submit-quotation-btn">Save Pricing</Button>
              </div>
            </DialogContent>
          </Dialog>
          {quotations.map(q => (
            <Card key={q.id} className="border border-border card-hover-lift" data-testid={`quotation-${q.id}`}>
              <CardContent className="p-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-heading font-semibold text-sm">Version {q.version}</span>
                  <Badge className="capitalize text-xs rounded-full bg-violet-50 text-violet-700 border-violet-200">{q.status}</Badge>
                </div>
                {q.items?.map((item, i) => (
                  <div key={`qi-${item.description}-${i}`} className="flex justify-between text-sm py-1.5 border-b border-border/50 last:border-0">
                    <span className="text-muted-foreground">{item.description}</span>
                    <span className="font-medium">{q.currency} {(item.amount || 0).toLocaleString()}</span>
                  </div>
                ))}
                <div className="flex justify-between text-sm pt-2 font-bold">
                  <span>Total</span><span className="text-voyage-blue">{q.currency} {(q.total_amount || 0).toLocaleString()}</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        {/* ── Follow-ups Tab ── */}
        <TabsContent value="followups" className="space-y-3">
          <Dialog open={showFollowup} onOpenChange={setShowFollowup}>
            <DialogTrigger asChild>
              <Button className="gap-2 gradient-blue hover:opacity-90 text-white shadow-md" data-testid="add-followup-btn"><Plus className="w-4 h-4" /> Schedule Follow-up</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-heading">Schedule Follow-up</DialogTitle><DialogDescription>Set a reminder to follow up.</DialogDescription></DialogHeader>
              <div className="space-y-4">
                <div><Label>Type</Label>
                  <Select value={newFollowup.type} onValueChange={v => setNewFollowup({...newFollowup, type: v})}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="call">Call</SelectItem><SelectItem value="email">Email</SelectItem>
                      <SelectItem value="whatsapp">WhatsApp</SelectItem><SelectItem value="meeting">Meeting</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Scheduled At</Label><Input type="datetime-local" value={newFollowup.scheduled_at} onChange={e => setNewFollowup({...newFollowup, scheduled_at: e.target.value})} className="mt-1" data-testid="followup-date-input" /></div>
                <div><Label>Notes</Label><Textarea value={newFollowup.notes} onChange={e => setNewFollowup({...newFollowup, notes: e.target.value})} className="mt-1" rows={2} /></div>
                <Button onClick={addFollowup} className="w-full gradient-blue hover:opacity-90 text-white" data-testid="submit-followup-btn">Schedule</Button>
              </div>
            </DialogContent>
          </Dialog>
          {followups.map(f => (
            <Card key={f.id} className="border border-border card-hover-lift" data-testid={`followup-${f.id}`}>
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${f.status === 'completed' ? 'bg-emerald-100' : 'bg-amber-100'}`}>
                      <Clock className={`w-3.5 h-3.5 ${f.status === 'completed' ? 'text-emerald-600' : 'text-amber-600'}`} />
                    </div>
                    <span className="text-sm font-medium capitalize">{f.type}</span>
                    <Badge className={`text-xs rounded-full capitalize ${f.status === 'completed' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>{f.status}</Badge>
                    {f.assigned_to_name && <Badge variant="outline" className="text-[10px]">Assigned: {f.assigned_to_name}</Badge>}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 ml-9">{new Date(f.scheduled_at).toLocaleString()}</p>
                  {f.notes && <p className="text-xs text-muted-foreground mt-0.5 ml-9">{f.notes}</p>}
                </div>
                {f.status === 'pending' && (
                  <Button size="sm" variant="outline" onClick={() => completeFollowup(f.id)} className="text-emerald-600 border-emerald-200 hover:bg-emerald-50" data-testid={`complete-followup-${f.id}`}>
                    <CheckCircle className="w-3.5 h-3.5 mr-1" /> Done
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        {/* ── Payments Tab ── */}
        <TabsContent value="payments" className="space-y-3">
          <Dialog open={showPayment} onOpenChange={setShowPayment}>
            <DialogTrigger asChild>
              <Button className="gap-2 gradient-blue hover:opacity-90 text-white shadow-md" data-testid="add-payment-btn"><Plus className="w-4 h-4" /> Record Payment</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-heading">Record Payment</DialogTitle><DialogDescription>Record a payment received.</DialogDescription></DialogHeader>
              <div className="space-y-4">
                <div><Label>Amount</Label><Input type="number" value={newPayment.amount} onChange={e => setNewPayment({...newPayment, amount: parseFloat(e.target.value) || 0})} className="mt-1" data-testid="payment-amount-input" /></div>
                <div><Label>Method</Label>
                  <Select value={newPayment.payment_method} onValueChange={v => setNewPayment({...newPayment, payment_method: v})}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bank_transfer">Bank Transfer</SelectItem><SelectItem value="upi">UPI</SelectItem>
                      <SelectItem value="card">Card</SelectItem><SelectItem value="cash">Cash</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Reference</Label><Input value={newPayment.reference} onChange={e => setNewPayment({...newPayment, reference: e.target.value})} className="mt-1" /></div>
                <div><Label>Notes</Label><Textarea value={newPayment.notes} onChange={e => setNewPayment({...newPayment, notes: e.target.value})} className="mt-1" rows={2} /></div>
                <Button onClick={addPayment} className="w-full gradient-blue hover:opacity-90 text-white" data-testid="submit-payment-btn">Record Payment</Button>
              </div>
            </DialogContent>
          </Dialog>
          {payments.map(p => (
            <Card key={p.id} className="border border-border card-hover-lift" data-testid={`payment-${p.id}`}>
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <span className="font-heading font-bold text-emerald-600">INR {(p.amount || 0).toLocaleString()}</span>
                  <p className="text-xs text-muted-foreground mt-0.5 capitalize">{p.payment_method?.replace('_', ' ')} {p.reference && `- ${p.reference}`}</p>
                </div>
                <Badge className={`capitalize text-xs rounded-full ${p.status === 'received' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>{p.status}</Badge>
              </CardContent>
            </Card>
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
}
